import { useEffect, useState } from "react";
import {
  APICreateContent,
  APIDeleteContent,
  APIGetContent,
  APIUpdateContent,
} from "../../config/utilities/APIs/contentApi";
import Title from "../../component/title/Title";
import { BusPolicyForm } from "./BusPolicyForm";
import Table from "../../component/table/Table";
import SnackBar from "../../component/Snackbar/Snackbar";
import DeactivationModal from "../../component/modal/DeactivationModal";
import DeleteModal from "../../component/modal/DeleteModal";
import NavBar from "../../component/navBar/NavBar";
import { useAuth } from "../../config/utilities/useAuth";
import "./BusPolicy.css";

const headers = [
  { key: "title", name: "Policy Title", sortable: false },
  { key: "version", name: "Version", sortable: false },
  { key: "effectiveDate", name: "Effective Date", sortable: false },
  { key: "status", name: "Status", sortable: false },
];

export default function BusPolicy() {
  const { privilege, token } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [fetchedBusPolicyData, setFetchedBusPolicyData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState({
    type: "bus-policy",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [input, setInput] = useState({
    title: "",
    version: "",
    content: "",
    effectiveDate: "",
    status: "active",
  });

  const [error, setError] = useState({
    title: false,
    version: false,
    content: false,
    effectiveDate: false,
  });

  const fetchBusPolicy = async (
    page = 1,
    filters = selectedFilters,
    searchTerm,
  ) => {
    setLoading(true);

    try {
      const response = await APIGetContent(
        page,
        filters,
        setLoading,
        institutionId,
        searchTerm,
      );

      if (response.status === 200) {
        const { data } = response.data;
        setFetchedBusPolicyData(data);
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching bus policy data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusPolicy(1, selectedFilters, searchTerm);
  }, [selectedFilters, searchTerm]);

  const handlePopoverAction = (action, rowId) => {
    const selectedRow = fetchedBusPolicyData.cmsData.find(
      (row) => row._id === rowId,
    );
    if (action === "edit") {
      setSelectedRowData(selectedRow);
      setAction("edit");
    } else if (action === "delete") {
      setSelectedRowData(selectedRow);
      setShowDeleteModal(true);
    } else if (action === "activate") {
      setSelectedRowData(selectedRow);
      setAction("activate");
      setShowDeactivationModal(true);
    } else if (action === "deactivate") {
      setSelectedRowData(selectedRow);
      setAction("deactivate");
      setShowDeactivationModal(true);
    } else {
      setSelectedRowData(selectedRow);
      setAction("view");
    }
  };

  const handleOpenCreate = () => {
    setAction("create");
  };

  const handleCreate = async () => {
    setLoading(true);
    const formattedInput = {
      type: "bus-policy",
      title: input.title,
      version: input.version,
      ...(input.effectiveDate &&
        input.effectiveDate !== "" && {
          effectiveDate: new Date(input.effectiveDate).toISOString(),
        }),
      content: input.content,
    };
    try {
      const response = await APICreateContent(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );
      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Create Successful");
        setSnackbarSubtitle("Bus Policy has been created successfully");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to create bus policy",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleEdit = async () => {
    if (!selectedRowData?._id) return;

    setLoading(true);

    const formattedInput = {
      id: selectedRowData._id,
      type: "bus-policy",
      title: input.title,
      version: input.version,
      ...(input.effectiveDate &&
        input.effectiveDate !== "" && {
          effectiveDate: new Date(input.effectiveDate).toISOString(),
        }),
      content: input.content,
    };

    try {
      const response = await APIUpdateContent(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Update Successful");
        setSnackbarSubtitle("Bus Policy has been updated successfully");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update bus policy",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleStatus = async (action) => {
    setLoading(true);

    const isActivate = action === "activate";
    const status = isActivate ? "active" : "deactive";
    const title = isActivate ? "Activation" : "Deactivation";

    const payload = {
      status,
      id: selectedRowData._id,
      type: "bus-policy",
    };

    try {
      const response = await APIUpdateContent(
        payload,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle(`${title} Successful`);
        setSnackbarSubtitle(
          `Bus Policy has been ${isActivate ? "activated" : "deactivated"} successfully`,
        );
        setSnackbarType("success");
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle(`${title} Failed`);
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleReset = async () => {
    setInput({
      title: "",
      version: "",
      content: "",
      effectiveDate: "",
      status: "active",
    });
    setError({
      title: false,
      version: false,
      content: false,
      effectiveDate: false,
    });
    setFetchedBusPolicyData([]);
    setShowDeleteModal(false);
    setSelectedRowData(null);
    setAction(null);
    setPageNumber(1);
    setShowDeactivationModal(false);
    await fetchBusPolicy(1, selectedFilters, searchTerm);
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const rowId = row._id;

    try {
      const response = await APIDeleteContent(
        rowId,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle("Bus Policy has been deleted successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to delete bus policy",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Delete Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <Title
            title="Edit Bus Policy"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "create":
        return (
          <Title
            title="Create Bus Policy"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        return (
          <Title
            title="View Bus Policy"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      default:
        return (
          <Title
            title="Bus Policy"
            subtitle="Set the contents for the bus policy on the parent app."
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <BusPolicyForm
            mode="edit"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
          />
        );
      case "create":
        return (
          <BusPolicyForm
            mode="create"
            onSubmit={handleCreate}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      case "view":
        return (
          <BusPolicyForm
            mode="view"
            existingData={selectedRowData}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      default:
        return (
          <Table
            data={fetchedBusPolicyData?.cmsData || []}
            searchText="Search by title, content, or version..."
            headers={headers}
            showEmptyHeader={true}
            showFilter={false}
            handlePopoverAction={handlePopoverAction}
            onClick={handleOpenCreate}
            module="busPolicy"
            loading={loading}
            showTableAction={
              role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
            }
            height="75vh"
            useBackendPagination={true}
            onPageChange={fetchBusPolicy}
            totalPages={fetchedBusPolicyData?.pagination?.totalPages || 1}
            currentPage={pageNumber}
            totalRecords={fetchedBusPolicyData?.pagination?.total || 0}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        );
    }
  };

  return (
    <div
      className={`main-page bus-policy-page ${
        !action ? "bus-policy-table-view" : ""
      }`}
    >
      <NavBar type="busPolicy" action={action} handleReset={handleReset} />
      <div className="content-area bus-policy-content-area">
        <div className="title-container mobile-hide-page-title">{renderTitle()}</div>
        <div className="main-content-container bus-policy-main-content-container">
          {renderContent()}
        </div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="busPolicy"
        loading={loading}
      />
      <DeactivationModal
        isOpen={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        loading={loading}
        onToggle={handleStatus}
        itemName={selectedRowData}
        module="busPolicy"
        action={action}
      />
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
}
